import { useCallback, useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import { useCardStore } from "../store/cardStore";
import { GRID_COLS, GRID_ROWS } from "../types";
import DraggableCard from "./DraggableCard";
import { sfx } from "../utils/sfx";
import { calculateCellSize } from "../utils/gridLayout";

export default function GridCanvas() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cellSize, setCellSize] = useState(24);
  const [activeId, setActiveId] = useState<number | null>(null);

  const { cards, editMode, updateCardPosition, saveCardPositions } =
    useCardStore();

  // Calculate cell size based on viewport dimensions (fit grid to screen)
  useEffect(() => {
    const updateSize = () => {
      // header ~56px, bottom bar ~80px, top padding ~24px
      const reserved = editMode ? 160 : 40;
      setCellSize(
        calculateCellSize(window.innerWidth, window.innerHeight, reserved),
      );
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [editMode]);

  // 150ms delay for touch/mouse to activate drag
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 150,
      tolerance: 5,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 150,
      tolerance: 5,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as number);
    sfx.hold();
    // Lock page scroll on mobile during drag
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, delta } = event;
      const cardId = active.id as number;
      const card = cards.find((c) => c.id === cardId);

      if (card) {
        const gridDeltaX = Math.round(delta.x / cellSize);
        const gridDeltaY = Math.round(delta.y / cellSize);

        const newX = card.grid_x + gridDeltaX;
        const newY = card.grid_y + gridDeltaY;

        updateCardPosition(cardId, newX, newY);
        saveCardPositions();
      }

      sfx.drop();

      setActiveId(null);
      // Unlock page scroll
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    },
    [cards, cellSize, updateCardPosition, saveCardPositions],
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  }, []);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div
        ref={containerRef}
        className="w-full flex justify-center px-4 sm:px-8 pt-4 sm:pt-6"
        style={{
          touchAction: editMode ? "none" : "auto",
          overscrollBehavior: editMode ? "none" : "auto",
        }}
      >
        <div
          className="relative"
          style={{
            width: cellSize * GRID_COLS,
            minHeight: cellSize * GRID_ROWS,
          }}
        >
          {/* Grid lines (only in edit mode) */}
          {editMode && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: `
                  linear-gradient(to right, var(--grid-line) 1px, transparent 1px),
                  linear-gradient(to bottom, var(--grid-line) 1px, transparent 1px)
                `,
                backgroundSize: `${cellSize}px ${cellSize}px`,
                borderRight: "1px solid var(--grid-line)",
                borderBottom: "1px solid var(--grid-line)",
              }}
            />
          )}

          {/* Empty state — shown in edit mode when there are no cards */}
          {editMode && cards.length === 0 && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-4 pointer-events-none select-none"
              style={{ minHeight: cellSize * 10 }}
            >
              <div className="flex flex-col items-center gap-3 opacity-40">
                <div className="w-12 h-12 border-2 border-dashed border-[var(--app-fg)] rounded-xl flex items-center justify-center">
                  <Plus size={24} className="text-[var(--app-fg)]" />
                </div>
                <p className="font-serif text-sm text-[var(--app-fg)] text-center leading-relaxed">
                  Your canvas is empty.
                  <br />
                  Tap <strong>+</strong> below to add your first card.
                </p>
              </div>
            </div>
          )}

          {/* Cards */}
          {cards.map((card) => (
            <DraggableCard
              key={card.id}
              card={card}
              layoutCellSize={cellSize}
              pointerCellSize={cellSize}
              editMode={editMode}
              isDragging={activeId === card.id}
            />
          ))}
        </div>
      </div>

      {/* Drag Overlay - empty to prevent diplopia; original card handles visual feedback */}
      <DragOverlay dropAnimation={null} />
    </DndContext>
  );
}
